import { observer } from 'mobx-react-lite';
import { useContext } from 'react';

import { YStack } from '@/components/ui/layout/layout';
import { FeedDensityContext } from '@/features/action-cards-view/feed/feedDensity';
import {
  FEED_ROW_SLOT_CLASS,
  FEED_ROW_SLOT_PADDING,
} from '@/features/action-cards-view/feed/feedStyles';
import { useTabs } from '@/features/tabs';
import { cn } from '@/lib/utils';

import { feedChatsStore, type FeedChatsStore } from '../feed-chats.store';

import { FeedChatRow } from './FeedChatRow';

import type { FeedChatView } from '../use-feed-chats';

type FeedChatRowsProps = {
  slot: keyof typeof FEED_ROW_SLOT_CLASS;
  store?: FeedChatsStore;
  working: readonly FeedChatView[];
  ready: readonly FeedChatView[];
  onOpenThread?: (threadId: string) => void;
};

const useOpenChat = (
  store: FeedChatsStore,
  onOpenThread?: (threadId: string) => void
) => {
  const { openTab } = useTabs();
  return (threadId: string) => {
    store.markSeen(threadId);
    if (onOpenThread) onOpenThread(threadId);
    else openTab({ path: `/chats/${threadId}` });
  };
};

export const FeedWorkingChatRows = observer(
  ({
    slot,
    working,
    onOpenThread,
    store = feedChatsStore,
  }: Pick<
    FeedChatRowsProps,
    'slot' | 'working' | 'store' | 'onOpenThread'
  >) => {
    const density = useContext(FeedDensityContext);
    const openChat = useOpenChat(store, onOpenThread);

    if (working.length === 0) return null;

    return (
      <YStack>
        {working.map((chat) => (
          <div
            key={chat.threadId}
            className={cn(
              FEED_ROW_SLOT_CLASS[slot],
              FEED_ROW_SLOT_PADDING[density]
            )}
          >
            <FeedChatRow chat={chat} onOpen={() => openChat(chat.threadId)} />
          </div>
        ))}
      </YStack>
    );
  }
);

export const FeedReadyChatRows = observer(
  ({
    slot,
    ready,
    onOpenThread,
    store = feedChatsStore,
  }: Pick<FeedChatRowsProps, 'slot' | 'ready' | 'store' | 'onOpenThread'>) => {
    const density = useContext(FeedDensityContext);
    const openChat = useOpenChat(store, onOpenThread);

    if (ready.length === 0) return null;

    return (
      <YStack>
        {ready.map((chat) => (
          <div
            key={chat.threadId}
            className={cn(
              FEED_ROW_SLOT_CLASS[slot],
              FEED_ROW_SLOT_PADDING[density]
            )}
          >
            <FeedChatRow
              chat={chat}
              onOpen={() => openChat(chat.threadId)}
              onDismiss={() => store.dismiss(chat.threadId)}
            />
          </div>
        ))}
      </YStack>
    );
  }
);
